// Delivery-area helpers.
//
// The backend resolves each pincode to a set of delivery terms (see
// DeliveryZoneService) and sends them as `serviceability` on every address
// and as `delivery` from GET /delivery/check. These helpers read that one
// shape so checkout, cart, the address form and the product page agree.

const LAST_PINCODE_KEY = 'fx:lastDeliveryPincode';

export const isValidPincode = (pincode) => /^[1-9][0-9]{5}$/.test(String(pincode || '').trim());

// An address with no terms attached (older cached data, or a profile-derived
// placeholder) is treated as deliverable; the server has the final say.
export const isServiceable = (delivery) => !delivery || delivery.serviceable !== false;

export const isCodAllowed = (delivery) => !delivery || delivery.cod_enabled !== false;

// Overlay the zone's shipping fee and free-shipping threshold on the
// store-wide settings. A zone that never ships free gets an infinite
// threshold, so `subtotal >= threshold` stays false without special-casing.
export const withDeliveryTerms = (appSettings, delivery) => {
  if (!delivery || delivery.serviceable === false || delivery.shipping_fee == null) return appSettings;

  return {
    ...appSettings,
    standardShippingFee: Number(delivery.shipping_fee) || 0,
    freeShippingThreshold: delivery.free_shipping_threshold == null
      ? Infinity
      : Number(delivery.free_shipping_threshold),
  };
};

// null allowed_slots = no restriction; an array = only those slot ids.
export const filterSlots = (slots, delivery) => {
  const list = Array.isArray(slots) ? slots : [];
  if (!delivery || !Array.isArray(delivery.allowed_slots)) return list;
  return list.filter((slot) => delivery.allowed_slots.includes(slot.id));
};

export const formatEta = (delivery) => {
  if (!delivery) return '';
  const { eta_min_days: min, eta_max_days: max } = delivery;
  if (min == null && max == null) return '';
  if (min != null && max != null && min !== max) return `Delivery in ${min}-${max} days`;
  const days = min ?? max;
  if (Number(days) === 0) return 'Delivery today';
  return `Delivery in ${days} day${Number(days) === 1 ? '' : 's'}`;
};

export const describeShipping = (delivery) => {
  if (!delivery || delivery.shipping_fee == null) return '';
  const fee = Number(delivery.shipping_fee) || 0;
  if (fee === 0) return 'Free delivery';
  return delivery.free_shipping_threshold == null
    ? `₹${fee.toFixed(0)} delivery`
    : `₹${fee.toFixed(0)} delivery, free above ₹${Number(delivery.free_shipping_threshold).toFixed(0)}`;
};

export const getLastPincode = () => {
  try {
    return localStorage.getItem(LAST_PINCODE_KEY) || '';
  } catch (_) {
    return '';
  }
};

export const rememberPincode = (pincode) => {
  try {
    localStorage.setItem(LAST_PINCODE_KEY, String(pincode));
  } catch (_) {
    // Storage unavailable (private mode): the check still works, it just isn't remembered.
  }
};
